/**
 * Types + pure helpers for the read-only "automated imports" views (customer
 * Uploads page and superadmin Extractions tab). The shapes mirror
 * lambda/shared/extraction-ledger.ts, which mirrors the data-extraction
 * repository's ledger — keep the three in sync.
 */

export type ExtractionRunStatus = 'running' | 'succeeded' | 'partial' | 'failed' | 'no_new_files';

export type ExtractionFileOutcome =
  | 'queued'
  | 'transferred'
  | 'failed'
  | 'skipped_already_ingested'
  | 'skipped_unstable'
  | 'skipped_in_progress'
  | 'excluded';

export interface ExtractionRunCounts {
  listed: number;
  excluded: number;
  skipped_already_ingested: number;
  skipped_unstable: number;
  skipped_in_progress: number;
  queued: number;
  transferred: number;
  failed: number;
}

export interface ExtractionRun {
  run_id: string;
  source_type: string;
  environment: string;
  trigger: 'scheduled' | 'manual';
  started_at: string;
  finished_at: string | null;
  status: ExtractionRunStatus;
  counts: ExtractionRunCounts;
  remote_paths: string[];
  s3_prefix: string | null;
  error: string | null;
}

export interface ExtractionRunFile {
  remote_path: string;
  size: number | null;
  mtime: string | null;
  outcome: ExtractionFileOutcome;
  reason: string | null;
  s3_key: string | null;
  error: string | null;
  updated_at: string;
}

/** The part of an extraction's definition every view shows (mirrors lambda/shared/extraction-ledger.ts CustomerExtractionDefinition). */
export interface ExtractionSummary {
  key: string;
  source_type: string;
  name: string;
  description: string;
  enabled: boolean;
  host: string;
  port: number;
  remote_paths: string[];
  schedule: {
    timezone: string;
    run: string;
    deadline: string;
    deadline_lookback_hours: number;
    run_timeout_minutes: number;
  };
}

/** Superadmin view: everything the pipeline published (mirrors ExtractionDefinition). */
export interface ExtractionDefinition extends ExtractionSummary {
  environment: string;
  recursive: boolean;
  stability: {
    strategy: string;
    min_age_minutes: number;
    exclude_patterns: string[];
  };
  s3_bucket: string;
  s3_prefix: string;
  connector_id: string;
  static_egress_ips: string[];
  orchestrator_function_arn: string;
}

const DAY_NAMES: Record<string, string> = {
  SUN: 'Sunday', MON: 'Monday', TUE: 'Tuesday', WED: 'Wednesday', THU: 'Thursday', FRI: 'Friday', SAT: 'Saturday',
  '1': 'Sunday', '2': 'Monday', '3': 'Tuesday', '4': 'Wednesday', '5': 'Thursday', '6': 'Friday', '7': 'Saturday',
};

/**
 * Human wording for the EventBridge Scheduler crons we generate
 * (`cron(min hour ? * DAY *)`, `cron(min hour * * ? *)`). Anything else is
 * returned verbatim so nothing is ever mis-described.
 */
export function describeCron(expression: string, timezone?: string): string {
  const m = expression.match(/^cron\((\d{1,2}) (\d{1,2}) (\S+) (\S+) (\S+) (\S+)\)$/);
  if (!m) return expression;
  const [, minute, hour, dayOfMonth, month, dayOfWeek] = m;
  if (month !== '*') return expression;
  const time = `${hour.padStart(2, '0')}:${minute.padStart(2, '0')}`;
  const tz = timezone ? ` (${timezone})` : '';
  if (dayOfMonth === '*' && (dayOfWeek === '?' || dayOfWeek === '*')) return `Every day at ${time}${tz}`;
  if (dayOfMonth === '?') {
    const days = dayOfWeek.split(',').map((d) => DAY_NAMES[d.toUpperCase()]);
    if (days.every(Boolean)) {
      const list = days.length === 1 ? `Every ${days[0]}` : `Every ${days.slice(0, -1).join(', ')} and ${days[days.length - 1]}`;
      return `${list} at ${time}${tz}`;
    }
    if (dayOfWeek.toUpperCase() === 'MON-FRI') return `Weekdays at ${time}${tz}`;
  }
  return expression;
}

export const RUN_STATUS_LABEL: Record<ExtractionRunStatus, string> = {
  running: 'Running',
  succeeded: 'Succeeded',
  partial: 'Partial',
  failed: 'Failed',
  no_new_files: 'No new files',
};

/** Colour class of the shared `.status-badge` for a run status. */
export function runStatusColor(status: ExtractionRunStatus): 'green' | 'yellow' | 'orange' | 'red' | 'gray' {
  switch (status) {
    case 'succeeded':
      return 'green';
    case 'running':
      return 'yellow';
    case 'partial':
      return 'orange';
    case 'failed':
      return 'red';
    default:
      return 'gray';
  }
}

export const FILE_OUTCOME_LABEL: Record<ExtractionFileOutcome, string> = {
  queued: 'In progress',
  transferred: 'Imported',
  failed: 'Failed',
  skipped_already_ingested: 'Already imported',
  skipped_unstable: 'Not ready yet',
  skipped_in_progress: 'Being imported by another run',
  excluded: 'Ignored',
};

// Surface failures and current-run work before files that were skipped.
const FILE_OUTCOME_SORT_RANK: Record<ExtractionFileOutcome, number> = {
  failed: 0,
  queued: 1,
  transferred: 2,
  excluded: 3,
  skipped_unstable: 4,
  skipped_in_progress: 5,
  skipped_already_ingested: 6,
};

export function fileOutcomeColor(outcome: ExtractionFileOutcome): 'green' | 'yellow' | 'red' | 'gray' {
  switch (outcome) {
    case 'transferred':
      return 'green';
    case 'queued':
      return 'yellow';
    case 'failed':
      return 'red';
    default:
      return 'gray';
  }
}

/** Human summary of a run's counts: "3 new · 2 imported · 1 failed · 5 already imported". */
export function summarizeRunCounts(counts: ExtractionRunCounts): string {
  const parts: string[] = [];
  parts.push(`${counts.listed} listed`);
  if (counts.queued) parts.push(`${counts.queued} new`);
  if (counts.transferred) parts.push(`${counts.transferred} imported`);
  if (counts.failed) parts.push(`${counts.failed} failed`);
  if (counts.skipped_already_ingested) parts.push(`${counts.skipped_already_ingested} already imported`);
  if (counts.skipped_unstable) parts.push(`${counts.skipped_unstable} not ready`);
  return parts.join(' · ');
}

/** Milliseconds between start and finish; null while running or when the timestamps are unusable. */
export function runDurationMs(run: Pick<ExtractionRun, 'started_at' | 'finished_at'>): number | null {
  if (!run.finished_at) return null;
  const ms = Date.parse(run.finished_at) - Date.parse(run.started_at);
  return Number.isFinite(ms) && ms >= 0 ? ms : null;
}

/** "4 min 12 s" between two ISO timestamps; null while running. */
export function runDuration(run: Pick<ExtractionRun, 'started_at' | 'finished_at'>): string | null {
  const ms = runDurationMs(run);
  if (ms === null) return null;
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s} s`;
  const m = Math.floor(s / 60);
  const rest = s % 60;
  if (m < 60) return rest ? `${m} min ${rest} s` : `${m} min`;
  const h = Math.floor(m / 60);
  return `${h} h ${m % 60} min`;
}

// ---------------------------------------------------------------- file tree

export interface RunFileTreeNode {
  /** Basename (directory or file). */
  name: string;
  /** Full remote path of this node. */
  path: string;
  /** Present for files only. */
  file?: ExtractionRunFile;
  children: RunFileTreeNode[];
}

/**
 * Builds a folder tree from remote paths ("/incoming/2026/a.csv" → incoming →
 * 2026 → a.csv). Directories sort first, naturally by name. Files sort by
 * outcome, then naturally by name, with current-run work before skipped files.
 * The remote directory structure is what the customer recognises, so it is
 * never flattened away.
 */
export function buildRunFileTree(files: ExtractionRunFile[]): RunFileTreeNode[] {
  const root: RunFileTreeNode = { name: '', path: '', children: [] };
  // Per-node index of children by name (files and directories keyed apart), so
  // construction is O(total segments) instead of a linear scan per segment —
  // a 15k-file flat directory would otherwise cost ~1e8 comparisons.
  const index = new Map<RunFileTreeNode, Map<string, RunFileTreeNode>>();
  const childrenOf = (node: RunFileTreeNode): Map<string, RunFileTreeNode> => {
    let m = index.get(node);
    if (!m) {
      m = new Map();
      index.set(node, m);
    }
    return m;
  };
  for (const file of files) {
    const segments = file.remote_path.split('/').filter((s) => s.length > 0);
    if (segments.length === 0) continue;
    let node = root;
    let path = '';
    segments.forEach((segment, i) => {
      path += `/${segment}`;
      const isLeaf = i === segments.length - 1;
      const key = isLeaf ? `f:${segment}` : `d:${segment}`;
      const siblings = childrenOf(node);
      let child = siblings.get(key);
      if (!child) {
        child = { name: segment, path, children: [] };
        if (isLeaf) child.file = file;
        node.children.push(child);
        siblings.set(key, child);
      } else if (isLeaf) {
        child.file = file;
      }
      node = child;
    });
  }
  sortTree(root);
  return root.children;
}

function sortTree(node: RunFileTreeNode): void {
  node.children.sort((a, b) => {
    const aDir = !a.file;
    const bDir = !b.file;
    if (aDir !== bDir) return aDir ? -1 : 1;
    if (a.file && b.file) {
      const outcomeOrder = FILE_OUTCOME_SORT_RANK[a.file.outcome] - FILE_OUTCOME_SORT_RANK[b.file.outcome];
      if (outcomeOrder !== 0) return outcomeOrder;
    }
    return a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: 'base' });
  });
  node.children.forEach(sortTree);
}

/** Count files under a node (for folder rows). */
export function countTreeFiles(node: RunFileTreeNode): { total: number; failed: number } {
  if (node.file) return { total: 1, failed: node.file.outcome === 'failed' ? 1 : 0 };
  return node.children.reduce(
    (acc, c) => {
      const n = countTreeFiles(c);
      return { total: acc.total + n.total, failed: acc.failed + n.failed };
    },
    { total: 0, failed: 0 },
  );
}
